<template>
  <div class="min-h-screen bg-white dark:bg-zinc-950">
    <!-- Hero Section -->
    <div
      class="relative px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-white via-gray-100 to-gray-200 dark:from-gray-900 dark:via-zinc-900 dark:to-black"
    >
      <!-- Decorative elements -->
      <div class="absolute inset-0 opacity-20 dark:opacity-15">
        <div
          class="absolute rounded-full top-5 sm:top-10 left-5 sm:left-10 w-40 sm:w-72 h-40 sm:h-72 bg-mainYellow/20 dark:bg-mainYellow/10 blur-3xl"
        ></div>
        <div
          class="absolute bottom-0 rounded-full right-5 sm:right-20 w-48 sm:w-96 h-48 sm:h-96 bg-mainYellow/10 dark:bg-mainYellow/5 blur-3xl"
        ></div>
      </div>

      <div class="relative mx-auto text-center max-w-4xl">
        <!-- Social Proof -->
        <div
          class="mb-6 text-sm font-semibold text-gray-700 dark:text-gray-300"
        >
          👥 Bought by 1,200+ music lovers this month
        </div>
        <h1
          class="mb-4 sm:mb-6 text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white"
        >
          Highfield Merch
        </h1>
        <p
          class="max-w-2xl mx-auto text-base sm:text-lg lg:text-xl leading-relaxed text-gray-700 dark:text-gray-300 px-2 mb-8"
        >
          Wear your passion. Express your music. Limited edition pieces designed
          for music lovers.
        </p>
        <!-- Trust Badges -->
        <div
          class="flex flex-wrap justify-center gap-6 text-sm font-semibold text-gray-700 dark:text-gray-300"
        >
          <div class="flex items-center gap-2">
            <span class="text-mainYellow">✓</span> Free Shipping
          </div>
          <div class="flex items-center gap-2">
            <span class="text-mainYellow">✓</span> Secure Payment
          </div>
          <div class="flex items-center gap-2">
            <span class="text-mainYellow">✓</span> 30-day Returns
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
      <div class="mx-auto max-w-7xl">
        <!-- Category Filters -->
        <div class="mb-12 flex flex-wrap justify-center gap-3">
          <button
            v-for="cat in categories"
            :key="cat"
            @click="activeCategory = cat"
            :class="[
              'px-6 py-2 rounded-lg font-semibold transition-all duration-300',
              activeCategory === cat
                ? 'bg-mainYellow text-gray-900'
                : 'border-2 border-gray-300 dark:border-zinc-600 text-gray-900 dark:text-white hover:border-mainYellow',
            ]"
          >
            {{ cat }}
          </button>
        </div>

        <!-- Products Grid -->
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-20"
        >
          <div
            v-for="product in filteredProducts"
            :key="product.id"
            @click="selectedProduct = product"
            class="group cursor-pointer"
          >
            <!-- Product Card -->
            <div
              class="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-zinc-700 transition-all duration-300 hover:border-mainYellow hover:shadow-2xl dark:hover:border-mainYellow bg-white dark:bg-zinc-900"
            >
              <!-- Image Container -->
              <div
                class="relative h-80 sm:h-96 overflow-hidden bg-gray-100 dark:bg-zinc-800"
              >
                <img
                  :src="product.image"
                  :alt="product.name"
                  class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <!-- Badge -->
                <div v-if="product.badge" class="absolute top-4 right-4">
                  <span
                    class="px-3 py-1 text-sm font-semibold rounded-full text-white bg-mainYellow"
                  >
                    {{ product.badge }}
                  </span>
                </div>
                <!-- Stock Indicator -->
                <div class="absolute bottom-4 left-4">
                  <span
                    :class="[
                      'px-3 py-1 text-xs font-bold rounded-full',
                      product.stock <= 3
                        ? 'bg-red-500 text-white'
                        : 'bg-green-500 text-white',
                    ]"
                  >
                    {{
                      product.stock <= 3
                        ? `Only ${product.stock} left!`
                        : `${product.stock} in stock`
                    }}
                  </span>
                </div>
              </div>

              <!-- Content -->
              <div class="p-5 sm:p-6">
                <div class="flex items-center justify-between mb-2">
                  <h3
                    class="text-lg sm:text-xl font-bold text-gray-900 dark:text-zinc-100 line-clamp-1 flex-1"
                  >
                    {{ product.name }}
                  </h3>
                </div>

                <!-- Rating -->
                <div class="flex items-center gap-2 mb-2">
                  <div class="flex text-mainYellow">
                    <span v-for="i in 5" :key="i">
                      {{ i <= product.rating ? "⭐" : "☆" }}
                    </span>
                  </div>
                  <span class="text-xs text-gray-600 dark:text-zinc-400"
                    >({{ product.reviews }} reviews)</span
                  >
                </div>

                <p
                  class="text-sm text-gray-600 dark:text-zinc-400 mb-4 line-clamp-2"
                >
                  {{ product.description }}
                </p>

                <!-- Colors/Sizes -->
                <div class="flex flex-wrap gap-2 mb-4">
                  <span
                    v-for="color in product.colors"
                    :key="color"
                    class="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-gray-300 dark:border-zinc-600"
                    :style="{ backgroundColor: color }"
                    :title="`Color: ${color}`"
                  ></span>
                </div>

                <!-- Price and CTA -->
                <div
                  class="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-zinc-700"
                >
                  <span class="text-2xl font-bold text-mainYellow">
                    €{{ product.price }}
                  </span>
                  <button
                    class="px-4 py-2 text-sm font-semibold text-white transition-all duration-300 rounded-lg bg-gray-900 hover:bg-gray-800 dark:bg-zinc-700 dark:hover:bg-zinc-600"
                  >
                    View
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Newsletter CTA -->
        <div
          class="bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-900 dark:via-zinc-900 dark:to-black rounded-2xl border border-gray-300 dark:border-mainYellow/20 p-8 sm:p-12 text-center mb-20"
        >
          <h2 class="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
            Get 10% Off Your First Order
          </h2>
          <p class="mb-6 text-gray-700 dark:text-gray-400 max-w-2xl mx-auto">
            Subscribe to our newsletter and receive exclusive updates, early
            access to new products, and special discounts.
          </p>
          <div class="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              v-model="email"
              type="email"
              placeholder="Enter your email"
              class="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-mainYellow"
            />
            <button
              class="px-6 py-3 bg-mainYellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-all duration-300"
            >
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Detail Modal -->
    <Dialog :open="!!selectedProduct" @update:open="selectedProduct = null">
      <DialogContent class="max-w-4xl max-h-[90vh] overflow-y-auto">
        <div
          v-if="selectedProduct"
          class="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          <!-- Image Carousel -->
          <div class="relative">
            <Carousel class="w-full">
              <CarouselContent>
                <CarouselItem
                  v-for="(img, index) in selectedProduct.images"
                  :key="index"
                  class="h-96 md:h-full"
                >
                  <div
                    class="h-full w-full flex items-center justify-center bg-gray-100 dark:bg-zinc-800 rounded-lg overflow-hidden"
                  >
                    <img
                      :src="img"
                      :alt="`${selectedProduct.name} - ${index + 1}`"
                      class="w-full h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious class="left-2" />
              <CarouselNext class="right-2" />
            </Carousel>
          </div>

          <!-- Product Details -->
          <div class="flex flex-col justify-start">
            <!-- Header -->
            <div class="mb-6">
              <div v-if="selectedProduct.badge" class="mb-3">
                <span
                  class="px-3 py-1 text-sm font-semibold rounded-full text-white bg-mainYellow"
                >
                  {{ selectedProduct.badge }}
                </span>
              </div>
              <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {{ selectedProduct.name }}
              </h2>

              <!-- Rating -->
              <div class="flex items-center gap-3 mb-4">
                <div class="flex text-mainYellow text-lg">
                  <span v-for="i in 5" :key="i">
                    {{ i <= selectedProduct.rating ? "⭐" : "☆" }}
                  </span>
                </div>
                <span class="text-sm text-gray-600 dark:text-zinc-400"
                  >({{ selectedProduct.reviews }} customer reviews)</span
                >
              </div>

              <p class="text-gray-600 dark:text-zinc-400 mb-4">
                {{ selectedProduct.description }}
              </p>

              <!-- Stock -->
              <div class="mb-4">
                <span
                  :class="[
                    'inline-block px-3 py-1 rounded text-sm font-bold',
                    selectedProduct.stock <= 3
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                  ]"
                >
                  {{
                    selectedProduct.stock <= 3
                      ? `Only ${selectedProduct.stock} left in stock!`
                      : `${selectedProduct.stock} in stock`
                  }}
                </span>
              </div>

              <div class="text-4xl font-bold text-mainYellow mb-6">
                €{{ selectedProduct.price }}
              </div>
            </div>

            <!-- Colors -->
            <div class="mb-6">
              <h3
                class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
              >
                Available Colors
              </h3>
              <div class="flex flex-wrap gap-3">
                <button
                  v-for="color in selectedProduct.colors"
                  :key="color"
                  class="relative group"
                >
                  <div
                    class="w-10 h-10 rounded-full border-2 border-gray-300 dark:border-zinc-600 hover:border-mainYellow transition-all duration-300"
                    :style="{ backgroundColor: color }"
                  ></div>
                  <span
                    class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-semibold text-white bg-gray-900 dark:bg-zinc-700 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
                  >
                    {{ color }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Sizes -->
            <div class="mb-6">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
                  Size
                </h3>
                <button
                  class="text-xs text-mainYellow hover:underline font-semibold"
                >
                  Size Guide
                </button>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="size in selectedProduct.sizes"
                  :key="size"
                  class="px-4 py-2 border-2 border-gray-300 dark:border-zinc-600 rounded-lg font-semibold text-gray-900 dark:text-white hover:border-mainYellow hover:text-mainYellow transition-all duration-300"
                >
                  {{ size }}
                </button>
              </div>
            </div>

            <!-- Quantity -->
            <div class="mb-6">
              <h3
                class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
              >
                Quantity
              </h3>
              <div
                class="flex items-center border-2 border-gray-300 dark:border-zinc-600 rounded-lg w-fit"
              >
                <button
                  @click="quantity = Math.max(1, quantity - 1)"
                  class="px-3 py-2 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700"
                >
                  −
                </button>
                <span
                  class="px-6 py-2 font-semibold text-gray-900 dark:text-white"
                >
                  {{ quantity }}
                </span>
                <button
                  @click="
                    quantity = Math.min(selectedProduct.stock, quantity + 1)
                  "
                  class="px-3 py-2 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700"
                >
                  +
                </button>
              </div>
            </div>

            <!-- Features -->
            <div class="mb-6">
              <h3
                class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
              >
                Features
              </h3>
              <ul class="space-y-2">
                <li
                  v-for="(feature, index) in selectedProduct.features"
                  :key="index"
                  class="flex items-center gap-2 text-gray-700 dark:text-zinc-300"
                >
                  <span class="text-mainYellow">✓</span>
                  {{ feature }}
                </li>
              </ul>
            </div>

            <!-- Add to Cart Button -->
            <button
              class="w-full px-6 py-3 text-lg font-bold text-white transition-all duration-300 rounded-lg bg-gray-900 hover:bg-gray-800 dark:bg-zinc-700 dark:hover:bg-zinc-600 hover:shadow-lg mb-3"
            >
              Add {{ quantity }} to Cart
            </button>

            <!-- Close Button -->
            <DialogClose
              class="mt-4 w-full px-6 py-2 text-sm font-semibold text-gray-900 dark:text-white border-2 border-gray-300 dark:border-zinc-600 rounded-lg hover:border-gray-400 dark:hover:border-zinc-500 transition-all duration-300"
            >
              Close
            </DialogClose>
          </div>

          <!-- Related Products -->
          <div
            v-if="relatedProducts.length"
            class="col-span-1 md:col-span-2 border-t border-gray-300 dark:border-zinc-700 pt-6 mt-6"
          >
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
              You might also like
            </h3>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div
                v-for="product in relatedProducts"
                :key="product.id"
                class="cursor-pointer group"
                @click="selectedProduct = product"
              >
                <div
                  class="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-800 mb-2 h-40"
                >
                  <img
                    :src="product.image"
                    :alt="product.name"
                    class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <p
                  class="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1"
                >
                  {{ product.name }}
                </p>
                <p class="text-mainYellow font-bold">€{{ product.price }}</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSeoMeta } from "#app";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const { t } = useI18n();

// SEO Meta
useSeoMeta({
  title: "Highfield Merch - Limited Edition Music Gear",
  ogTitle: "Highfield Merch - Limited Edition Music Gear",
  description:
    "Exclusive merchandise and apparel for music lovers. High-quality t-shirts, hoodies, and accessories.",
  ogDescription:
    "Exclusive merchandise and apparel for music lovers. High-quality t-shirts, hoodies, and accessories.",
  ogImage:
    "https://isto.pt/cdn/shop/files/Classic_TShirt_Black_2_22a615df-0a48-4b9d-accb-98e77071d72d.webp?v=1765462274",
});

// Products Array
const products = [
  {
    id: 1,
    name: "Highfield Classic T-Shirt",
    description: "Premium cotton t-shirt with embroidered Highfield logo",
    price: "24.99",
    image:
      "https://isto.pt/cdn/shop/files/Classic_TShirt_Black_2_22a615df-0a48-4b9d-accb-98e77071d72d.webp?v=1765462274",
    images: [
      "https://isto.pt/cdn/shop/files/Classic_TShirt_Black_2_22a615df-0a48-4b9d-accb-98e77071d72d.webp?v=1765462274",
      "https://isto.pt/cdn/shop/files/Classic_TShirt_Black_2_22a615df-0a48-4b9d-accb-98e77071d72d.webp?v=1765462274",
    ],
    colors: ["#000000", "#FFFFFF", "#1E40AF", "#DC2626"],
    sizes: ["XS", "S", "M", "L", "XL", "XXL"],
    features: [
      "100% Organic Cotton",
      "Comfortable fit",
      "Embroidered logo",
      "Breathable fabric",
    ],
    badge: "Best Seller",
    category: "T-Shirts",
    stock: 45,
    rating: 5,
    reviews: 128,
  },
  {
    id: 2,
    name: "Highfield Premium Hoodie",
    description:
      "Warm and cozy hoodie perfect for studio sessions or casual wear",
    price: "54.99",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTUJHn_nKRdoRvXmQ-QsvbB2z7xpWrM-fEhPw&s",
    images: [
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTUJHn_nKRdoRvXmQ-QsvbB2z7xpWrM-fEhPw&s",
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTUJHn_nKRdoRvXmQ-QsvbB2z7xpWrM-fEhPw&s",
    ],
    colors: ["#000000", "#4B5563", "#1E40AF"],
    sizes: ["XS", "S", "M", "L", "XL", "XXL"],
    features: [
      "Heavy weight fabric",
      "Warm lining",
      "Zip pockets",
      "Adjustable drawstring",
    ],
    badge: "New",
    category: "Hoodies",
    stock: 32,
    rating: 5,
    reviews: 89,
  },
  {
    id: 3,
    name: "Highfield Baseball Cap",
    description: "Classic baseball cap with adjustable back strap",
    price: "19.99",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQRGIiWPe71f6c8YZ7gZViL0QELpQkXOFIXng&s",
    images: [
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQRGIiWPe71f6c8YZ7gZViL0QELpQkXOFIXng&s",
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQRGIiWPe71f6c8YZ7gZViL0QELpQkXOFIXng&s",
    ],
    colors: ["#000000", "#FFFFFF", "#FFD700"],
    sizes: ["One Size"],
    features: [
      "Cotton twill",
      "Adjustable strap",
      "Curved brim",
      "Embroidered front",
    ],
    badge: null,
    category: "Accessories",
    stock: 67,
    rating: 4,
    reviews: 54,
  },
  {
    id: 4,
    name: "Highfield Crew Neck Sweatshirt",
    description: "Soft crew neck sweatshirt for everyday comfort",
    price: "39.99",
    image:
      "https://maufeitio.pt/onlineshop/170615-thickbox_default/fred-perry-malha-classic-crew-neck-k9601-navy.jpg",
    images: [
      "https://maufeitio.pt/onlineshop/170615-thickbox_default/fred-perry-malha-classic-crew-neck-k9601-navy.jpg",
      "https://maufeitio.pt/onlineshop/170615-thickbox_default/fred-perry-malha-classic-crew-neck-k9601-navy.jpg",
    ],
    colors: ["#000000", "#4B5563", "#DC2626"],
    sizes: ["XS", "S", "M", "L", "XL", "XXL"],
    features: [
      "Soft fleece",
      "Comfortable fit",
      "Ribbed cuffs",
      "Front pocket",
    ],
    badge: "Popular",
    category: "Hoodies",
    stock: 28,
    rating: 4,
    reviews: 67,
  },
  {
    id: 5,
    name: "Highfield Canvas Tote Bag",
    description:
      "Spacious canvas bag perfect for transporting gear or daily use",
    price: "29.99",
    image:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR2yfsns6LWtsIoceeAInY80Kj2MnaR0tE_0Q&s",
    images: [
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR2yfsns6LWtsIoceeAInY80Kj2MnaR0tE_0Q&s",
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR2yfsns6LWtsIoceeAInY80Kj2MnaR0tE_0Q&s",
    ],
    colors: ["#000000", "#FFFFFF", "#8B7355"],
    sizes: ["One Size"],
    features: [
      "Durable canvas",
      "Large capacity",
      "Double handles",
      "Screen-printed logo",
    ],
    badge: null,
    category: "Accessories",
    stock: 81,
    rating: 4,
    reviews: 76,
  },
  {
    id: 6,
    name: "Highfield Classic Beanie",
    description: "Warm beanie for cold weather sessions",
    price: "16.99",
    image:
      "https://idioma.world/cdn/shop/files/Classic-Beanie-Black-Web_1500x1500.jpg?v=1731351659",
    images: [
      "https://idioma.world/cdn/shop/files/Classic-Beanie-Black-Web_1500x1500.jpg?v=1731351659",
      "https://idioma.world/cdn/shop/files/Classic-Beanie-Black-Web_1500x1500.jpg?v=1731351659",
    ],
    colors: ["#000000", "#1E40AF", "#DC2626"],
    sizes: ["One Size"],
    features: [
      "Knit acrylic blend",
      "Soft and stretchy",
      "Fold-over cuff",
      "Warm lining",
    ],
    badge: null,
    category: "Accessories",
    stock: 15,
    rating: 5,
    reviews: 43,
  },
];

// State
const selectedProduct = ref(null);
const activeCategory = ref("All");
const quantity = ref(1);
const email = ref("");

// Categories
const categories = ["All", "T-Shirts", "Hoodies", "Accessories"];

// Computed
const filteredProducts = computed(() => {
  if (activeCategory.value === "All") {
    return products;
  }
  return products.filter((p) => p.category === activeCategory.value);
});

const relatedProducts = computed(() => {
  if (!selectedProduct.value) return [];
  return products
    .filter(
      (p) =>
        p.id !== selectedProduct.value.id &&
        p.category === selectedProduct.value.category,
    )
    .slice(0, 3);
});
</script>
